import { StepHelpers } from"@shared/components/Stepper";
import { Badge } from"@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from"@shared/ui/card";
import { ValueLabel } from "@shared/components/ValueLabel";
import { Button } from"@shared/ui/button";
import { Checkbox } from"@shared/ui/checkbox";
import { Label } from"@shared/ui/label";
import { RadioGroup, RadioGroupItem } from"@shared/ui/radio-group";
import { format } from"date-fns";
import {
 Bell,
 Calendar,
 CheckCircle,
 ChevronLeft,
 FileText,
 Mail,
 Phone,
 Send,
 User,
} from"lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SelectedAccountsBanner } from "../../../../../shared/components/SelectedAccountsBanner";
import { useCreateDisconnectionRequest } from "../../../hooks";
import { useCreateBulkReconnectRequest } from '../../../../reconnect/hooks';
import { useTranslation } from 'react-i18next';
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { queryClient } from "@shared/api/queries/queryClient";
import { useConsumerDetail } from '../../../../../shared/hook';

interface ReviewPaymentStepProps {
 remoteUtilityId: number;
 storageKey: string;
 logTimeUntilStep?: (step: number | string) => void;
 stopTracking?: () => void;
 stepHelpers?: StepHelpers;
 currentStepIndex?: number;
 onNext?: () => void;
 onPrevious?: () => void;
 setValidationError?: (error: string) => void;
 clearValidationError?: () => void;
}

interface ReviewData {
 customer?: {
 name: string;
 accountNumber: string;
 type: string;
 email: string;
 phone: string;
 address: string;
 id: number;
 };
 request?: {
 requestType: string;
 utilitySupportRequest: string;
 priority: string;
 scheduledDate: Date | null;
 reason: string;
 timeSlot: string;
 reasonLabel: string;
 timeSlotLabel: string;
 consumerRemark: string;
 };
}

export function ReviewPaymentStep({
 remoteUtilityId,
 storageKey,
 logTimeUntilStep,
 stopTracking,
 stepHelpers,
 currentStepIndex = 2,
 onNext,
 onPrevious,
 setValidationError,
 clearValidationError,
}: ReviewPaymentStepProps) {
 const { t } = useTranslation();
 const [searchParams] = useSearchParams();
 const isMultiAccount = searchParams.get("multiAccount") === "true";
 const selectedConsumerIdFromParams = searchParams.get("selectedConsumerId");
 const isSubAccount = searchParams.get("isSubAccount") === "true";
 const urlConsumerId = searchParams.get("consumerId");

 const { data: consumerDetailResult } = useConsumerDetail({
  remote_utility_id: remoteUtilityId,
  consumer_id: urlConsumerId || undefined
 }, { enabled: !!urlConsumerId && isSubAccount });
 const consumerDetail = consumerDetailResult?.result;

 const [reviewData, setReviewData] = useState<ReviewData>({});
 const dataLoadedRef = useRef(false);
 const { mutate: createDisconnectionRequest, isPending: isSubmittingSingle } =
 useCreateDisconnectionRequest();
 const { mutate: createBulkDisconnectionRequest, isPending: isSubmittingBulk } =
 useCreateBulkReconnectRequest();
 const isSubmitting = isSubmittingSingle || isSubmittingBulk;

 // Helper function to save step data using stepHelpers
 const saveStepData = useCallback(
 (data: any) => {
  if (!stepHelpers) return;

  try {
  const stepData = {
   ...data,
   timestamp: new Date().toISOString(),
   stepIndex: currentStepIndex,
  };

  stepHelpers.setStepData(currentStepIndex, stepData);
  console.log("Review step data saved:", stepData);
  } catch (error) {
  console.error("Failed to save review step data:", error);
  }
 },
 [stepHelpers, currentStepIndex]
 );

 // Helper function to get step data using stepHelpers
 const getStepData = useCallback(
 (stepIndex: number) => {
  if (!stepHelpers) return null;

  try {
  return stepHelpers.getStepData(stepIndex);
  } catch (error) {
  console.error(`Failed to get step data for step ${stepIndex}:`, error);
  return null;
  }
 },
 [stepHelpers]
 );

 // Helper function to get all steps data
 const getAllStepsData = useCallback(() => {
    let customerData = null;
    let requestData = null;
    let notificationData = null;

    if (currentStepIndex === 2) {
      customerData = getStepData(0);
      requestData = getStepData(1);
    } else {
      requestData = getStepData(0);
      customerData = requestData?.customerData || null;
    }

    return { customerData, requestData, notificationData };
  }, [getStepData, currentStepIndex]);

  console.log('all', getAllStepsData());

 // Load data from all previous steps
 useEffect(() => {
 if (stepHelpers && !dataLoadedRef.current) {
  try {
  const { customerData, requestData, notificationData } =
   getAllStepsData();

  console.log("Loading review data from all steps:", {
   customerData,
   requestData,
   notificationData,
  });

  const compiledReviewData: ReviewData = {};

  // Load customer data
  if (isMultiAccount && customerData && customerData.selectedIds) {
    compiledReviewData.customer = {
      id: 0,
      name: t("Multiple Accounts"),
      accountNumber: `${customerData.selectedIds.length} ${t("Accounts Selected")}`,
      type: "Multiple",
      email: "-",
      phone: "-",
      address: "-",
      utilityServices: "",
      selectedIds: customerData.selectedIds,
      selectedAccounts: customerData.selectedAccounts
    } as any;
  } else if (isSubAccount && consumerDetail) {
    compiledReviewData.customer = {
      id: consumerDetail.id || consumerDetail.consumerId,
      name: `${consumerDetail.firstName || 'NA'} ${consumerDetail.lastName || 'nA'}`.trim(),
      accountNumber: consumerDetail.consumerNo || "NA",
      type: consumerDetail.categoryDisplay || "NA",
      email: consumerDetail.email || "NA",
      phone: consumerDetail.contactNumber || "NA",
      address: consumerDetail.addressData?.service ? `${consumerDetail.addressData.service.address || ''} ${consumerDetail.addressData.service.unit || ''}`.trim() : "-",
      utilityServices: "NA"
    } as any;
  } else if (customerData) {
   compiledReviewData.customer = {
   id: customerData.customerId,
   name: customerData.customerName ||"",
   accountNumber: customerData.accountNumber ||"",
   type:
    customerData.consumerMetadata?.categoryDisplay ||
    customerData.selectedCustomer?.customerType ||
  "Standard",
   email: customerData.contactEmail ||"",
   phone: customerData.contactPhone ||"",
   address: customerData.serviceAddress?.address || customerData.serviceAddress ||"",
   utilityServices:
    customerData.consumerMetadata?.utilityServices?.join(",") ||"",
   };
  } else {
    // Fallback if not available
    const { remoteConsumerNumber } = getLoginDataFromStorage() || {};
    compiledReviewData.customer = {
      id: 0,
      name: "N/A",
      accountNumber: remoteConsumerNumber || "N/A",
      type: "Standard",
      email: "N/A",
      phone: "N/A",
      address: "N/A",
      utilityServices: ""
    };
  }

  // Override id if selectedConsumerId exists in URL for scenario 2
  if (selectedConsumerIdFromParams && compiledReviewData.customer) {
    compiledReviewData.customer.id = Number(selectedConsumerIdFromParams);
  }

  // Load request details data
  if (requestData) {
   compiledReviewData.request = {
   requestType: requestData.requestType ||"disconnection",
   utilitySupportRequest: requestData.reason ||"",
   reason: requestData.reason ||"",
   reasonLabel: requestData.reasonLabel ||"",
   timeSlotLabel: requestData.timeSlotLabel ||"",
   priority: requestData.priority ||"medium",
   scheduledDate: requestData.scheduledDate
    ? new Date(requestData.scheduledDate)
    : null,
   timeSlot: requestData.timeSlot ||"",
   consumerRemark: requestData.consumerRemark ||"",
   };
  }

  setReviewData(compiledReviewData);
  dataLoadedRef.current = true;
  } catch (error) {
  console.error("Failed to load review data:", error);
  dataLoadedRef.current = true;
  }
 }
 }, [stepHelpers, currentStepIndex, getAllStepsData]);

 const handleSubmit = async () => {
 try {
  if (isMultiAccount) {
   const payload = {
     remote_utility_id: Number(remoteUtilityId),
     request_type: "Disconnect Permanent",
     consumer_ids: (reviewData?.customer as any)?.selectedIds?.map(Number) || [],
     reason: reviewData?.request?.reasonLabel || reviewData?.request?.reason || "",
     preferred_date: reviewData?.request?.scheduledDate ? format(reviewData.request.scheduledDate, "yyyy-MM-dd") : "",
     preferred_time_slot: reviewData?.request?.timeSlot ? Number(reviewData.request.timeSlot) : 0,
     additional_notes: reviewData?.request?.consumerRemark || "",
     priority: 2,
     source: 1
   };

   saveStepData({ reviewData, reviewed: true, submittedAt: new Date().toISOString(), isStepComplete: true });
   if (clearValidationError) clearValidationError();

   console.log("Submitting bulk disconnection request payload:", payload);
   createBulkDisconnectionRequest(payload, { onSuccess: () => {
    {
            queryClient.refetchQueries({ queryKey: ["cx", "consumerRequests", "list"] });
                 if (onNext) onNext();

    }
   } });
  } else {
   const payload = {
    request_date: new Date().toISOString(),
    consumer_remark: reviewData?.request?.consumerRemark || "",
    request_type: "Disconnect Permanent",
    source: 1,
    consumer: reviewData?.customer?.id || "",
    remote_utility_id: remoteUtilityId.toString(),
    utility_support_request: reviewData?.request?.utilitySupportRequest || "",
    reason: reviewData?.request?.utilitySupportRequest || "",
    additional_data: {
     utility_support_request_version: "Version-0",
     preferred_time_slot: reviewData?.request?.timeSlot ? Number(reviewData.request.timeSlot) : 1
    }
   };
   // Save final review data
   saveStepData({
   reviewData,
   reviewed: true,
   submittedAt: new Date().toISOString(),
   isStepComplete: true,
   });
   // Clear any errors
   if (clearValidationError) {
   clearValidationError();
   }
   // Create and submit payload
   console.log("Submitting disconnection request payload:", payload);

   createDisconnectionRequest(payload, {
   onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["cx", "consumerRequests", "list"] });
           if (onNext) onNext();

   },
   });
  }
  stepHelpers?.resetAllData();
 } catch (error) {
  console.error("Failed to submit request:", error);
  if (setValidationError) {
  setValidationError("Failed to submit request. Please try again.");
  }
 }
 };

 return (
 <div className="space-y-6">
  <SelectedAccountsBanner stepHelpers={stepHelpers} />
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
  {/* Customer Information */}
  <Card >
   <CardHeader className="pb-4">
   <CardTitle className="flex items-center gap-2 text-lg">
    <User className="h-5 w-5 text-orange-500"/>
    {t("Customer Information")}
   </CardTitle>
   </CardHeader>
   <CardContent className="space-y-4">
    {isMultiAccount ? (
     <div className="space-y-2">
      <ValueLabel label={t("Selected Accounts")} value={
        <div className="flex flex-wrap gap-2 mt-2">
         {(reviewData.customer as any)?.selectedAccounts?.map((acc: any) => (
          <Badge key={acc.id} variant="secondary">
           {acc.name} - {acc.consumerNo}
          </Badge>
         ))}
        </div>
      } />
     </div>
    ) : (
     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ValueLabel label={t("Name")} value={reviewData.customer?.name} />
      <ValueLabel
      label={t("Account Number")}
      value={
       <Badge variant="outline">
       {reviewData.customer?.accountNumber}
       </Badge>
      }
      />
      <ValueLabel
      label={t("Account Type")}
      value={
       <Badge variant="outline"className="">
       {reviewData.request?.ownershipDisplay ||"Individual"}
       </Badge>
      }
      />
      <ValueLabel
      label={t("Category")}
      value={
       <Badge variant="outline"className="">
       {reviewData.customer?.type ||"INACTIVE"}
       </Badge>
      }
      />
      <ValueLabel 
      label={t("Email")}
      value={
       <span 
       className="block truncate max-w-[200px]"
       title={reviewData.customer?.email}
       >
       {reviewData.customer?.email}
       </span>
      } 
      />
      <ValueLabel label={t("Phone")} value={reviewData.customer?.phone} />
     </div>
    )}
   </CardContent>
  </Card>

  {/* Request Details */}
  <Card >
   <CardHeader className="pb-4">
   <CardTitle className="flex items-center gap-2 text-lg">
    <FileText className="h-5 w-5 text-green-500"/>
    {t("Request Details")}
   </CardTitle>
   </CardHeader>
   <CardContent className="space-y-4">
   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <ValueLabel
    label={t("Request Type")}
    value={t("Disconnection")}
    />

    <ValueLabel
    label={t("Reason")}
    value={reviewData.request?.reasonLabel}
    />
    <ValueLabel
    label={t("Preferred Date")}
    value={reviewData.request?.scheduledDate ? format(reviewData.request.scheduledDate,"MMM dd, yyyy") : t("Not Set")}
    />
    <ValueLabel
    label={t("Time Slot")}
    value={reviewData.request?.timeSlotLabel}
    />
    {reviewData.request?.consumerRemark && (
    <div className="col-span-1 md:col-span-2">
     <ValueLabel
     label={t("Additional Notes")}
     value={<span>"{reviewData.request.consumerRemark}"</span>}
     />
    </div>
    )}
   </div>
   </CardContent>
  </Card>
  </div>

  {/* Action Buttons */}
  <div className="flex justify-between pt-6">
  <Button
   onClick={onPrevious}
   variant="outline"
   disabled={isSubmitting}
   className="flex items-center gap-2"
  >
   <ChevronLeft size={16} />
   <span>{t("Previous")}</span>
  </Button>

  <Button
   onClick={handleSubmit}
   disabled={isSubmitting}
   className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
  >
   {isSubmitting ? (
   <>
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
    <span>{t("Submitting...")}</span>
   </>
   ) : (
   <>
    <Send size={16} />
    <span>{t("Submit Request")}</span>
   </>
   )}
  </Button>
  </div>
 </div>
 );
}
